import type { SymptomKey, RecoveryDailyLog, SymptomRiskLevel } from "../types/recovery";

// ─── Types ────────────────────────────────────────────────────────────────────

export type DailyRiskLevel = "none" | "ok" | "normal" | "monitor" | "alert";

export type SymptomDaySummary = {
  dateId: string;
  weekdayLabel: string;
  risk: DailyRiskLevel;
  alertSymptoms: SymptomKey[];
  log?: RecoveryDailyLog;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const RISK_WEIGHT: Record<DailyRiskLevel, number> = {
  none: 0,
  ok: 1,
  normal: 1,
  monitor: 2,
  alert: 3,
};

const SYMPTOM_KEYS: SymptomKey[] = [
  "perinealPain",
  "cSectionWound",
  "fatigue",
  "breastfeedingPain",
  "bladderControl",
  "bowelFunction",
  "pelvicHeaviness",
  "painAndMovement",
];

const isSymptomRiskLevel = (value: string): value is SymptomRiskLevel =>
  value === "normal" || value === "monitor" || value === "alert";

// ─── Date Helpers ─────────────────────────────────────────────────────────────

export const formatWeekdayLetter = (dateId: string): string => {
  const parts = dateId.split("-");
  if (parts.length !== 3) return "";
  const [y, m, d] = parts.map(Number);
  if (!y || !m || !d) return "";
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { weekday: "short" }).charAt(0);
};

export const getLastNDateIds = (n: number): string[] => {
  const today = new Date();
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (n - 1 - i));
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
};

export const getLast7DaysRange = (days: SymptomDaySummary[]): string => {
  if (days.length === 0) return "";
  const last7 = days.slice(-7);
  const firstDay = new Date(last7[0].dateId.replace(/-/g, "/"));
  const lastDay = new Date(last7[last7.length - 1].dateId.replace(/-/g, "/"));
  if (isNaN(firstDay.getTime()) || isNaN(lastDay.getTime())) return "";
  const month = lastDay.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
  return `${firstDay.getDate()}–${lastDay.getDate()} ${month}`;
};

// ─── Risk Helpers ─────────────────────────────────────────────────────────────

export const maxRisk = (
  current: DailyRiskLevel,
  next: SymptomRiskLevel | undefined
): DailyRiskLevel => {
  if (!next) return current;
  const mapped: DailyRiskLevel =
    next === "alert" ? "alert" : next === "monitor" ? "monitor" : next === "normal" ? "normal" : "ok";
  return RISK_WEIGHT[mapped] >= RISK_WEIGHT[current] ? mapped : current;
};

export const getSymptomRisk = (
  log: RecoveryDailyLog | null,
  symptomKey: SymptomKey | "vaginalBleeding"
): DailyRiskLevel => {
  if (!log) return "none";

  if (symptomKey === "vaginalBleeding") {
    const r = log.symptoms?.vaginalBleeding?.overallRisk?.toLowerCase().trim();
    if (r === "alert") return "alert";
    if (r === "monitor") return "monitor";
    if (r === "normal") return "normal";
    if (r === "ok") return "ok";
    return "none";
  }

  const symptomRecord = log.symptoms as Partial<Record<SymptomKey, { risk?: string }>>;
  const symptom = symptomRecord?.[symptomKey];
  if (!symptom?.risk) return "none";

  const r = symptom.risk.toLowerCase().trim();
  if (r === "alert") return "alert";
  if (r === "monitor") return "monitor";
  if (r === "normal") return "normal";
  if (r === "ok") return "ok";
  return "none";
};

export const computeDailyRisk = (log: RecoveryDailyLog | null): DailyRiskLevel => {
  if (!log) return "none";
  if (log.symptomAlerts?.hasAnyAlert) return "alert";

  const symptoms = log.symptoms;
  if (!symptoms || Object.keys(symptoms).length === 0) return "none";

  const symptomRecord = symptoms as Partial<Record<SymptomKey, { risk?: string }>>;

  return SYMPTOM_KEYS.reduce<DailyRiskLevel>((daily, key) => {
    const s = symptomRecord[key];
    return s?.risk && isSymptomRiskLevel(s.risk) ? maxRisk(daily, s.risk) : daily;
  }, "ok");
};

export const riskToBgColor = (risk: DailyRiskLevel): string => {
  if (risk === "alert") return "#FCB3AD";
  if (risk === "monitor") return "#FFE0C0";
  if (risk === "ok" || risk === "normal") return "#D1E0B7";
  return "#C0C0C0"; // "none" — no data
};
